import React, { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';
import { Blue500, Green600, ICON_COLOR, Mint700, Pink400, Purple500, Yellow700 } from '@zep-us/zus/tokens';
import { BoltFillIcon, OrderIcon, TextLongLeftIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import '@/shared/quiz/events.quiz-build';
import { trackEvent } from '@zep/logger/tracking';
import { CheckSmallCircleFillIcon, MessageLinesFillIcon, PlusSmallIcon, SquareGridSemiFillIcon } from '@/icons';
import { WithPlanBadge } from '@/shared/components/Badge/PlanBadge';
import { LocalizedOxTypeIcon } from '@/shared/components/LocalizedOxTypeIcon';
import { useUpsellingGuard, useUserPlanManager } from '@/shared/plan';
import { Button, type ButtonProps } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { QuizTypeHoverPreview } from '@/views/build/modules/quiz-type-preview';
import { useBuildInfoContext } from '../context/BuildInfoProvider';
import { getBuildType } from '../util/getBuildType';
import { HoverPreviewGroup } from './HoverPreview/HoverPreview';
import { type QuizType } from './type';
import { useQuizList } from './useQuizList';
type BuildQuizButtonsLayoutMode = 'page' | 'ai-shell';

/** 18px 은 ZUS 간격 토큰에 없어 임의값 — 앱의 다른 모서리 플랜 배지와 같은 크기 */
const PLAN_BADGE_CLASS_NAME = 'pointer-events-none -right-2xs -top-2xs size-[18px] translate-x-0 translate-y-0';
const PLAN_BADGE_GLYPH = <BoltFillIcon width={12} height={12} fill={ICON_COLOR.inverse} aria-hidden />;
export const BuildQuizButtons = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'> & {
  layoutMode?: BuildQuizButtonsLayoutMode;
}>((props, ref) => {
  const {
    className,
    layoutMode = 'page',
    ...rest
  } = props;
  const {
    t
  } = useTranslation(['common', 'build']);
  const {
    handleCreate
  } = useQuizList();
  const upsellingGuard = useUpsellingGuard();
  const {
    userPlanManager
  } = useUserPlanManager();
  const featurePlanType = userPlanManager.getFeaturePlanType('MATCH');
  const orderFeaturePlanType = userPlanManager.getFeaturePlanType('ORDER');
  const {
    action
  } = useBuildInfoContext();
  const handleButtonClick = (type: QuizType) => {
    trackEvent('add_quizItem', {
      type,
      position: 'button'
    });
    handleCreate(type);
  };
  const trackTypePreviewView = (type: QuizType) => {
    trackEvent('quiz_build_question_type_preview_view', {
      question_type: type,
      place: 'add_question_button',
      build_type: getBuildType(action)
    });
  };
  return <HoverPreviewGroup>
      <div {...rest} className={cn('flex w-full flex-col gap-xs', layoutMode === 'ai-shell' ? 'min-[1025px]:grid min-[1025px]:grid-cols-3 min-[1025px]:gap-sm' : 'tablet-sm:grid tablet-sm:grid-cols-3 tablet-sm:gap-sm', className)} ref={ref}>
        <QuizTypeHoverPreview type="multiple" onPreviewOpen={trackTypePreviewView}>
          <BuildQuizItemButton onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }} onClick={() => handleButtonClick('multiple')} text={t('build:build.multiple.button')} icon={<CheckSmallCircleFillIcon fill={Green600} />} />
        </QuizTypeHoverPreview>
        <QuizTypeHoverPreview type="subjective" onPreviewOpen={trackTypePreviewView}>
          <BuildQuizItemButton onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }} onClick={() => handleButtonClick('subjective')} text={t('build:build.subjective.button')} icon={<MessageLinesFillIcon fill={Blue500} />} />
        </QuizTypeHoverPreview>
        <QuizTypeHoverPreview type="ox" onPreviewOpen={trackTypePreviewView}>
          <BuildQuizItemButton onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }} onClick={() => handleButtonClick('ox')} text={t('build:build.ox.button')} icon={<LocalizedOxTypeIcon size="md" fill={Purple500} />} />
        </QuizTypeHoverPreview>
        <QuizTypeHoverPreview type="essay" onPreviewOpen={trackTypePreviewView}>
          <BuildQuizItemButton onMouseDown={e => {
          e.stopPropagation();
          e.preventDefault();
        }} onClick={() => handleButtonClick('essay')} text={t('build:build.quizType.essayQuestion')} icon={<TextLongLeftIcon size="md" fill={Pink400} />} />
        </QuizTypeHoverPreview>
        <WithPlanBadge isBadgeVisible={true} wrapperClassName="w-full" planType={featurePlanType} className={PLAN_BADGE_CLASS_NAME} glyph={PLAN_BADGE_GLYPH}>
          <QuizTypeHoverPreview type="match" onPreviewOpen={trackTypePreviewView}>
            <BuildQuizItemButton onMouseDown={e => {
            e.stopPropagation();
            e.preventDefault();
          }} onClick={() => upsellingGuard(() => handleButtonClick('match'), {
            feature: 'MATCH',
            from: 'match',
            initialPlanType: 'PRO'
          })} text={t('common:quizCard.typeBadge.match')} icon={<SquareGridSemiFillIcon size="md" fill={Yellow700} />} />
          </QuizTypeHoverPreview>
        </WithPlanBadge>
        <WithPlanBadge isBadgeVisible={true} wrapperClassName="w-full" planType={orderFeaturePlanType} className={PLAN_BADGE_CLASS_NAME} glyph={PLAN_BADGE_GLYPH}>
          <QuizTypeHoverPreview type="order" onPreviewOpen={trackTypePreviewView}>
            <BuildQuizItemButton onMouseDown={e => {
            e.stopPropagation();
            e.preventDefault();
          }} onClick={() => upsellingGuard(() => handleButtonClick('order'), {
            feature: 'ORDER',
            from: 'order',
            initialPlanType: 'PRO'
          })} text={t('common:quizCard.typeBadge.order')} icon={<OrderIcon size="md" fill={Mint700} />} />
          </QuizTypeHoverPreview>
        </WithPlanBadge>
      </div>
    </HoverPreviewGroup>;
});
BuildQuizButtons.displayName = 'BuildQuizButtons';
type BuildQuizItemButtonProps = ButtonProps & {
  text: string;
  icon: ReactNode;
};
const BuildQuizItemButton = forwardRef<HTMLButtonElement, BuildQuizItemButtonProps>((props, ref) => {
  const {
    text,
    icon,
    onClick,
    className,
    variant = 'contained',
    ...rest
  } = props;
  return <Button {...rest} className={cn('flex w-full justify-center border-opacity-assistive shadow-xs focus:focus-ring', className, variant === 'contained' && 'bg-default')} size={'lg'} variant={variant} intent={'neutral'} onClick={onClick} ref={ref}>
      {icon}
      <span>{text}</span>
      <PlusSmallIcon from={ICON_COLOR.default} />
    </Button>;
});
BuildQuizItemButton.displayName = 'BuildQuizItemButton';